import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { getAllCompetitions, createCompetition, updateCompetition, deleteCompetition } from "@/lib/api";
import { toast } from "sonner";

const initialFormData = {
  name: "",
  season: "",
  is_active: true,
};

export default function AdminCompetitions() {
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCompetitions();
  }, []);

  const fetchCompetitions = async () => {
    try {
      const response = await getAllCompetitions();
      setCompetitions(response.data);
    } catch (error) {
      console.error("Error fetching competitions:", error);
      toast.error("Kon competities niet laden");
    } finally {
      setLoading(false);
    }
  };

  const openNewDialog = () => {
    setFormData(initialFormData);
    setEditingId(null);
    setDialogOpen(true);
  };

  const handleEdit = (competition) => {
    setFormData({
      name: competition.name,
      season: competition.season,
      is_active: competition.is_active,
    });
    setEditingId(competition.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm("Weet je zeker dat je deze competitie wilt verwijderen?")) return;
    try {
      await deleteCompetition(id);
      toast.success("Competitie succesvol verwijderd");
      fetchCompetitions();
    } catch (error) {
      console.error("Error deleting competition:", error);
      toast.error("Kon competitie niet verwijderen");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await updateCompetition(editingId, formData);
        toast.success("Competitie succesvol bijgewerkt");
      } else {
        await createCompetition(formData);
        toast.success("Competitie succesvol toegevoegd");
      }
      setDialogOpen(false);
      fetchCompetitions();
    } catch (error) {
      console.error("Error saving competition:", error);
      toast.error("Kon competitie niet opslaan");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (checked) => {
    setFormData((prev) => ({ ...prev, is_active: checked }));
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Trophy className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">Competities</h1>
            <p className="text-slate-600">Beheer competities en seizoenen</p>
          </div>
        </div>
        <Button onClick={openNewDialog} className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="h-4 w-4 mr-2" />
          Competitie Toevoegen
        </Button>
      </div>

      <Card className="bg-white border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-600">Laden...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50 border-slate-200">
                  <TableHead className="text-slate-600">Naam</TableHead>
                  <TableHead className="text-slate-600">Seizoen</TableHead>
                  <TableHead className="text-slate-600">Status</TableHead>
                  <TableHead className="text-right text-slate-600">Acties</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {competitions.length > 0 ? (
                  competitions.map((competition) => (
                    <TableRow key={competition.id} className="border-slate-200">
                      <TableCell className="font-medium text-slate-900">{competition.name}</TableCell>
                      <TableCell className="text-slate-600">{competition.season}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs ${competition.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}>
                          {competition.is_active ? "Actief" : "Inactief"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleEdit(competition)} className="border-slate-200">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDelete(competition.id)} className="border-slate-200 text-red-600 hover:text-red-700">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                      Geen competities gevonden. Voeg je eerste competitie toe!
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">{editingId ? "Competitie Bewerken" : "Competitie Toevoegen"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-700">Naam *</Label>
              <Input id="name" name="name" value={formData.name} onChange={handleChange} required className="border-slate-300" placeholder="bijv. Belgian Futsal League" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="season" className="text-slate-700">Seizoen *</Label>
              <Input id="season" name="season" value={formData.season} onChange={handleChange} required className="border-slate-300" placeholder="bijv. 2024-2025" />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-slate-700">Actief</Label>
              <Switch checked={formData.is_active} onCheckedChange={handleSwitchChange} />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-300">Annuleren</Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">{saving ? "Opslaan..." : "Opslaan"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
